import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import clsx from 'clsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import useInterval from '../../../libraries/useinterval';

import Grid from '@mui/material/Grid';
import Backdrop from '@mui/material/Backdrop';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Chip from '@mui/material/Chip';

import DeleteIcon from '@mui/icons-material/Delete';
import PublicIcon from '@mui/icons-material/Public';
import ListIcon from '@mui/icons-material/List';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TransformIcon from '@mui/icons-material/Transform';

import { format as D3format } from 'd3';
import Moment from 'moment';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import {
    get as _get,
    set as _set,
    uniqBy as _uniqBy,
    debounce as _debounce
} from 'lodash';
import { arrayMoveImmutable as arrayMove } from 'array-move';

import BubbleTabs from '../../../components/bubbletabs';
import { EMPTY_FILTER } from '../../../components/filter/filterbuilder';
import TagList from '../../../components/tags/taglist';
import IncidentResponseList from '../../../components/incident-response-list';
import Tooltip from '../../../components/styled/tooltip';
import TimelineChartWithSlider, { CalculateProgress } from '../../../components/charts/timelineslider.js';
import DonutChart from '../../../components/charts/donutchart';
import GeoMap from '../../../components/charts/geomap';
import PopupMenu from '../../../components/popupmenu';
import IPAddress from '../../../components/ipaddress';
import FileHash from '../../../components/filehash';
import SensorName from '../../../components/sensorname';
import { BTJob } from '../../../components/bt-jobs';
import Tag from '../../../components/tags/tag';
import Mitre, { MitreList } from '../../../components/mitre';
import CountBar from '../../../components/charts/countbar';
import Sparkline from '../../../components/charts/sparkline';

import _MetadataService from '../../../data/metadata-service';
import DataContext from '../../../data/data-context';
import Auth, { ProfileContext, ParamsSerializer } from '../../../data/auth-service';
import { GroupsContext, Severities } from '../../../data/groups-service';
import PolicyService from '../../../data/policy-service';
import IncidentService from '../../../data/incident-service';
import ReportsService from '../../../data/reports-service';
import usePrevious from '../../../libraries/useprevious';
import useWindowSize from '../../../libraries/usewindowsize';
import { MergeNodes } from '../../../d3/utils';
import CMCsDataService, { CmcContext } from '../../../data/cmcs-service';
import {
    CopyToClipboard,
    GetISODateFromQuick,
    SaveFile,
    ToArray,
    GetBTUUID,
    NoKeyword,
} from '../../../libraries/helpers';
// import { useWhatChangedEffect } from '../../../libraries/withwhyrender';
import { LoadingOverlay, NoCMCLoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';
import MetadataDetail from './metadata-detail';
import TableToolbar, {
    ListMode,
    DetailMode,
    HistoryMode,
    FivetupleMode,
    ThreetupleMode,
    MapMode,
} from '../components/table-toolbar';
import GeomapToolbar from '../components/geomap-toolbar';
import ReportsToast from '../components/reports-toast';
import TableFooter from '../components/footer';
import FilterBar, {
    QUICK_HOUR,
    // QUICK_DAY,
    // QUICK_WEEK,
    QUICK_JOB,
    BTMODE_BT,
    BTMODE_LIVE,
    BTMODE_NONE,
    GetRangeFromJobs,
    GetMomentRangeFromJobs,
    GetCreatedFromJobs,
    GetBacktestingEnglish,
    // MaxDate,
    // MinDate,
} from '../components/filter-bar';
import ColumnSelector from './column-selector.js';
import { neutrals2 } from '../../../data/colors';

const useStyles = makeStyles(theme => ({
    content: {
        flexGrow: 1,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginRight: -10,
        position: 'relative',
    },
    table: {
        '& .MuiDataGrid-main': {
            zIndex: theme.zIndex.drawer + 5,
            backgroundColor: theme.palette.background.default,
        },
        '& .backtesting': {
            backgroundColor: theme.palette.backtesting.table,
        },
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
    datePicker: {
        maxWidth: 162,
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    stealRoom: {
        paddingLeft: 13,
        paddingRight: 13,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    mapview: {
        border: `1px solid ${neutrals2[600]}`,
        borderRadius: 4,
        marginTop: 8,
    },
    englishGreen: {
        color: theme.palette.tertiary.main,
    },
    englishtooltip: {
        maxWidth: 500,
    },
    english: {
        position: 'relative',
        '& div': {
            display: 'block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            fontSize: 'small',
            color: theme.palette.tertiary.main,
            position: 'absolute',
            top: 5,
            left: 10,
            width: '95%',
        }
    },
    mapped: {
        verticalAlign: 'middle',
        paddingRight: 5,
    },
    nowrap: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
}));

const modes = [
    { key: ListMode, icon: <ListIcon/>, title: 'list metadata' },
    { key: DetailMode, icon: <VisibilityIcon/>, title: 'view details' },
    { key: HistoryMode, icon: <RestoreIcon/>, title: 'recently viewed' },
    { key: FivetupleMode, icon: '5', title: 'group by 5-tuple' },
    { key: ThreetupleMode, icon: '3', title: 'group by client-server ID' },
    { key: MapMode, icon: <PublicIcon/>, title: 'show map' },
];

const defaultWidths = {
    count: 100,
    activity: 300,
    remove: 50,
    'bro_log.id.orig_h': 160,
    'bro_log.id.resp_h': 160,
    timestamp: 160,
};

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
const MTDT_LIMIT = 500;
const REFRESH_RATE = 20000;
const TIMELINE_HEIGHT = MC2CHECK ? 10 : 110;
const PAGE_HEADER = 130;

const Metadata = ({ profile = 'metadata', ...props }) => {
    const toolbarRef = useRef(null);
    const updatedRef = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const tableAPIRef = useApiRef();
    const location = useLocation();
    const navigate = useNavigate();
    const toastId = useRef(null);
    const { width, height } = useWindowSize();

    const { updateProfile, getProfile } = useContext(ProfileContext);
    const {
        assets,
        tags,
        sensorNames,
        clearMtdtMitre,
        setMtdtMitre,
        clearMtdtsMitre,
        setMtdtsMitre,
        mtdtMappings: mappings,
        setMtdtMappings: setMappings,
        backtestingJobs,
        backtestingJobsLite,
        systemSettings,
    } = useContext(DataContext);

    const perms = Auth.permissions.get('Metadata');
    const incidentPerms = Auth.permissions.get('Incidents');
    const gatorPerms = Auth.permissions.get('DataTuningPolicy');

    // data state
    const [data, setData] = useState([]);
    const [tagging, setTagging] = useState(false);
    const [quick, setQuick] = useState(QUICK_HOUR);
    const [sort, setSort] = useState('timestamp desc');
    const [search, setSearch] = useState();
    const [range, setRange] = useState([undefined, undefined]);
    const [filter, setFilter] = useState();
    const [groups, setGroups] = useState([]);
    const [tagFilters, setTagFilters] = useState([]);
    const [tagsOp, setTagsOp] = useState();
    const [hasAlerts, setHasAlerts] = useState(false);
    const [sources, setSources] = useState(props.sources || ['conn']);
    const [backtesting, setBacktesting] = useState(BTMODE_NONE);
    const [selectedBTJobs, setSelectedBTJobs] = useState([]);
    const [savedFilters, setSavedFilters] = useState([]);

    // page state
    const [init, setInit] = useState();
    const [mode, setMode] = useState([ListMode]);
    const [currentMtdt, setCurrentMtdt] = useState();
    const [prevMtdt, setPrevMtdt] = useState();
    const [nextMtdt, setNextMtdt] = useState();
    const [showTags, setShowTags] = useState(false);
    const [showMitre, setShowMitre] = useState(false);
    const [haveIncidents, setHaveIncidents] = useState(false);
    const [showIncidents, setShowIncidents] = useState(false);
    const [showColumnsSel, setShowColumnsSel] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [showFilterTimeout, setShowFilterTimeout] = useState();
    const [fields, setFields] = useState([]);
    const [tableHeight, setTableHeight] = useState(800);
    const [selection, setSelection] = useState();
    const [grouping, setGrouping] = useState(false);
    const [columns, setColumns] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState({});
    const [widths, setWidths] = useState(defaultWidths);
    const [hideColumns, setHideColumns] = useState({});
    const [english, setEnglish] = useState('');
    const [savedFilter, setSavedFilter] = useState('');
    const [hideFilterbar, setHideFilterbar] = useState(true);
    const [hideTimelinebar, setHideTimelinebar] = useState(true);
    const [canTune, setCanTune] = useState(false);
    const [wantPolicy, setWantPolicy] = useState();

    // paging state
    const [total, setTotal] = useState(0);
    const [asof, setAsof] = useState();
    const [msg, setMsg] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [done, setDone] = useState(false);
    const [pause, setPause] = useState(false);
    const [refreshRate, setRefreshRate] = useState(REFRESH_RATE);
    const [refresh, setRefresh] = useState();
    const [wantSource, setWantSource] = useState();

    // charts state
    const [timeline, setTimeline] = useState({ data: [] });
    const [geodata, setGeodata] = useState({});
    const [groupedData, setGroupedData] = useState([]);
    const [groupKeys, setGroupKeys] = useState([]);
    const [maxGroup, setMaxGroup] = useState(0);
    const [MetadataService] = useState(props.service || _MetadataService);

    // mc2 cmc state - cmc context is set at the app level
    // cmc is an object { uuid, name } - stored in page profile as uuid
    const { cmc, setCmc } = useContext(CmcContext);
    // cmcs is an object { uuid: name, uuid: name }
    const [cmcs, setCmcs] = useState();
    // selectedCmcs is a string array [uuid, uuid, uuid]
    const [selectedCmcs, setSelectedCmcs] = useState();

    const getCmc = useCallback(uuid => {
        if (!(cmcs || {})[uuid]) return undefined;
        return { uuid, name: cmcs[uuid] };
    }, [cmcs]);

    // slow down and give the user a chance to finish typing their dates
    // useMemo instead of useCallback for less rendering
    const setRangeDebounce = useMemo(() => _debounce(setRange, 420), []);
    const isBacktesting = useMemo(() => backtesting === BTMODE_BT, [backtesting]);
    const currentMode = useMemo(() => mode.length < 1 ? ListMode : mode[mode.length - 1], [mode]);

    // rested handles this switch over
    const defaultSort = useMemo(() => {
        return 'timestamp desc';
    }, []);

    const jobCreated = useMemo(() => {
        return GetCreatedFromJobs(selectedBTJobs, backtestingJobsLite);
    }, [selectedBTJobs, backtestingJobsLite]);

    const untag = useCallback((d, tag) => {
        if (groups.length > 0) {
            let f = MetadataService.filterFromGroups(d, groups);
            if (filter && (filter.nodes || []).length > 0) {
                f = { operator: 'And', nodes: [filter, f] };
            }
            MetadataService.untagMtdts(tag, {
                search,
                start: range[0],
                end: range[1],
                filter: f,
                tagFilters,
                tagsOp,
                sources,
                hasAlerts,
                cmc,
                backtesting,
                selectedBTJobs,
                jobCreated,
            }).catch(handleError);
        } else {
            MetadataService.untagMtdt(tag, d, cmc).catch(handleError);
        }
        // remove immediately from selected row
        const i = _get(d, '_source.bricata.tag', []).findIndex(t => t === tag);
        if (i >= 0) {
            d._source.bricata.tag.splice(i, 1);
            tableAPIRef.current.updateRows([{ _source: d._source }]);
        }
    }, [
        filter,
        groups,
        range,
        search,
        sources,
        tableAPIRef,
        tagFilters,
        tagsOp,
        hasAlerts,
        cmc,
        MetadataService,
        backtesting,
        selectedBTJobs,
        jobCreated,
    ]);

    const unmitre = useCallback((d, tag) => {
        clearMtdtMitre(_get(d, '_source.bricata.event_uuid'), cmc);

        // remove immediately from selected row
        d._source.bricata.mitre_category = '';
        tableAPIRef.current.updateRows([{ ...d }]);
    }, [clearMtdtMitre, cmc, tableAPIRef]);

    const mitre = useCallback((d, mitre) => {
        setMtdtMitre(_get(d, '_source.bricata.event_uuid'), mitre, cmc);

        // update immediately from selected row
        d._source.bricata.mitre_category = mitre;
        tableAPIRef.current.updateRows([{ _source: d._source }]);
    }, [setMtdtMitre, cmc, tableAPIRef]);

    const getMappedValues = useCallback((m, row) => {
        return m.map(f => MetadataService.columnsMap[f])
            .reduce((a, c) => {
                if (!c) return a;
                return [
                    ...a,
                    ...ToArray(MetadataService.value(row, c))
                        .map(f => ({ title: c.title, value: (f || '').trim() })),
                ];
            }, []);
    }, [MetadataService]);

    const getCellRenderers = useCallback(() => ({
        // mapped columns
        'bro_log.id.orig_h': p => {
            let _v = [p.row];
            let c;
            const m = mappings['bro_log.id.orig_h'] || ['bro_log.id.orig_h'];
            if (typeof p.row === 'object') {
                _v = getMappedValues(m, p.row);
                c = _get(p.row, '_source.bricata.src_location.country');
            }
            return _v.map((v, i) => {
                return v.value ? <span key={`val_${i}`} style={{ paddingLeft: 15 }}>
                    <IPAddress
                        key={`val_${i}`}
                        country={c}
                        address={v.value}
                        tipPrefix={`${v.title}: `}
                        uuid={_get(p.row, '_source.bricata.sensor_uuid')}/>
                </span> : '';
            });
        },
        'bro_log.id.resp_h': p => {
            let _v = [p.row];
            let c;
            const m = mappings['bro_log.id.resp_h'] || ['bro_log.id.resp_h'];
            if (typeof p.row === 'object') {
                _v = getMappedValues(m, p.row);
                c = _get(p.row, '_source.bricata.dest_location.country');
            }
            return _v.map((v, i) => {
                return v.value ? <span key={`val_${i}`} style={{ paddingLeft: 15 }}>
                    <IPAddress
                        key={`val_${i}`}
                        country={c}
                        address={v.value}
                        tipPrefix={`${v.title}: `}
                        uuid={_get(p.row, '_source.bricata.sensor_uuid')}/>
                </span> : '';
            });
        },
        // fu zeek
        'bro_log.id.orig_p': p => {
            let _v = [p.row];
            const m = mappings['bro_log.id.orig_p'] || ['id.orig_p'];
            if (typeof p.row === 'object') {
                _v = getMappedValues(m, p.row);
            }
            return _v.map((v, i) => v.value ? <Tooltip
                key={`val_${i}`}
                arrow
                placement="top"
                title={v.title}>
                <span style={{ paddingRight: 10 }}>{v.value}</span>
            </Tooltip> : ''
            );
        },
        'bro_log.id.resp_p': p => {
            let _v = [p.row];
            const m = mappings['bro_log.id.resp_p'] || ['id.resp_p'];
            if (typeof p.row === 'object') {
                _v = getMappedValues(m, p.row);
            }
            return _v.map((v, i) => v.value ? <Tooltip
                key={`val_${i}`}
                arrow
                placement="top"
                title={v.title}>
                <span style={{ paddingRight: 10 }}>{v.value}</span>
            </Tooltip> : ''
            );
        },
        'bricata.sensor_uuid': p => {
            const v = typeof p.row === 'object' ? _get(p.row, '_source.bricata.sensor_uuid') : p.row;
            return <SensorName uuid={'' + v} />;
        },
        bt_uuid: p => {
            const btuuid = GetBTUUID(p.row, '_source.bt_uuid');
            return btuuid ? <BTJob uuid={'' + btuuid} /> : '';
        },
        'bricata.tag': p => {
            const v = typeof p.row === 'object' ? _get(p.row, '_source.bricata.tag') : p.row;
            if (!v) return '';
            const handler = t => untag(p.row, t);
            return v.map((t, i) =>
                <span key={t + i}>
                    <Tag
                        key={t + i}
                        tag={t}
                        onUntag={handler}
                        untagLabel={'untag'}/>
                </span>
            );
        },
        'bricata.mitre_category': p => {
            const v = typeof p.row === 'object' ? _get(p.row, '_source.bricata.mitre_category') : p.row;
            if (!v) return '';
            return <Mitre
                mitre={v}
                onUnmitre={p.row.count ? undefined : v => unmitre(p.row, v)}
                onUpdateMitre={p.row.count ? undefined : v => mitre(p.row, v)}/>;
        },
        'bro_log.md5': p => {
            const v = typeof p.row === 'object' ? _get(p.row, '_source.bro_log.md5') : p.row;
            if (!v) return '';
            return <FileHash hash={v} title='MD5'/>;
        },
        'bro_log.sha1': p => {
            const v = typeof p.row === 'object' ? _get(p.row, '_source.bro_log.sha1') : p.row;
            if (!v) return '';
            return <FileHash hash={v} title='SHA1'/>;
        },
        'bro_log.sha256': p => {
            const v = typeof p.row === 'object' ? _get(p.row, '_source.bro_log.sha256') : p.row;
            if (!v) return '';
            return <FileHash hash={v} title='SHA256'/>;
        },
        count: p => {
            const v = typeof p.row === 'object' ? p.row.count : p.row;
            if (!v) return '';
            return <span style={{ textAlign: 'left', lineHeight: 1.5 }}>
                {D3format(',')(v)}
                <div style={{ lineHeight: 0 }}>
                    <CountBar value={+v} colorKey={p.row.key}/>
                </div>
            </span>;
        },
        activity: p => {
            if (!p.row || !p.row.key) return;
            return <span style={{ lineHeight: 1.5 }}>
                <Sparkline
                    type='line'
                    dataservice={MetadataService}
                    data={p.row.timeline}
                    uuid={p.row.key}
                    colorKey={p.row.key}/>
            </span>;
        }
    }), [untag, mappings, unmitre, mitre, MetadataService, getMappedValues]);

    const isMappedColumn = useCallback(v => {
        if (!mappings[v] || !mappings[v].length) return false;
        if (mappings[v].length > 1) return true;
        return (mappings[v][0] !== v);
    }, [mappings]);

    // header rendering for mapped values
    const getHeaderRenderers = useCallback(() => {
        const mappedHeader = c => {
            const ms = mappings[c.variable].map(m => `${MetadataService.columnsMap[m].title}`);
            return (<Tooltip
                arrow placement="top"
                size="small"
                title={ms.join(', ')}>
                <span><TransformIcon color='primary' className={classes.mapped} size='small'/>{c.title}</span>
            </Tooltip>);
        };
        return {
            'bro_log.id.orig_h': p => {
                const c = MetadataService.columnsMap['bro_log.id.orig_h'];
                return isMappedColumn(c.variable) ? mappedHeader(c) : undefined;
            },
            'bro_log.id.orig_p': p => {
                const c = MetadataService.columnsMap['bro_log.id.orig_p'];
                return isMappedColumn(c.variable) ? mappedHeader(c) : undefined;
            },
            'bro_log.id.resp_h': p => {
                const c = MetadataService.columnsMap['bro_log.id.resp_h'];
                return isMappedColumn(c.variable) ? mappedHeader(c) : undefined;
            },
            'bro_log.id.resp_p': p => {
                const c = MetadataService.columnsMap['bro_log.id.resp_p'];
                return isMappedColumn(c.variable) ? mappedHeader(c) : undefined;
            }
        };
    }, [classes, mappings, isMappedColumn, MetadataService.columnsMap]);

    const shouldHide = useCallback((col, hide) => {
        if (col.variable === 'remove') return currentMode !== HistoryMode;
        return !!hideColumns[col.variable] || (col.grouping ? groups.length < 1 : hide);
    }, [currentMode, hideColumns, groups]);

    const shouldColSelHide = useCallback(col => {
        if (col.variable === 'remove') return true;
        return col.grouping && groups.length < 1;
    }, [groups]);

    const defaultColumns = useCallback(() => {
        const ww = widths || defaultWidths;
        return fields
            .filter(f => !f.tableHidden)
            .map(f => ({
                ...f,
                sortable: f.indexed || f.sortable,
                headerName: f.title,
                valueGetter: p => MetadataService.value(p.row, f),
                type: 'string',
                hide: shouldHide(f, f.hide),
                columnSelectorHidden: shouldColSelHide(f),
                renderHeader: getHeaderRenderers()[f.variable],
                renderCell: getCellRenderers()[f.variable],
                width: ww[f.variable] || 100,
                ...getTableTypes(theme)[f.type]
            }));
    }, [fields, theme, widths, shouldHide, shouldColSelHide, getHeaderRenderers, getCellRenderers, MetadataService]);

    const resetPage = useCallback(async () => {
        setQuick(QUICK_HOUR);
        setSort(defaultSort);
        setGroups([]);
        setSearch('');
        setTagsOp('Or');
        setHasAlerts(false);
        setTagFilters([]);
        setFilter(EMPTY_FILTER);
        setRange([GetISODateFromQuick(QUICK_HOUR), undefined]);
        setGrouping(false);
        setSources(['conn']);
        setBacktesting(_get(systemSettings, 'support.backtesting.ui', false)
            ? BTMODE_LIVE : BTMODE_NONE);
        setSelectedBTJobs([]);
        setMode([ListMode]);
        setColumns(defaultColumns());
        setRefresh();
    }, [defaultColumns, systemSettings, defaultSort]);

    const reloadColumns = useCallback((state) => {
        // defense against the dark arts of empty profiles
        state = state || {};
        // columns preferences are stashed in the profile but are not part of saved filters for consistency
        const ww = { ...defaultWidths, ...(widths || {}), ...(state.widths || {}) };
        const hd = { ...hideColumns, ...(state.hideColumns || {}) };
        state.columns = state.columns || {};
        setColumns(current => {
            if (Object.keys(state.columns || {}).length > 0 || (state.groups || []).length > 1) {
                return fields
                    .filter(f => !f.tableHidden)
                    .map(f => ({
                        ...f,
                        sortable: (state.groups || []).length > 1 ? (f.variable === 'count') : (f.indexed || f.sortable),
                        headerName: f.title,
                        valueGetter: p => MetadataService.value(p.row, f),
                        type: 'string',
                        hide: state.columns[f.variable] === undefined || hd[f.variable],
                        columnSelectorHidden: shouldColSelHide(f),
                        renderCell: getCellRenderers()[f.variable],
                        renderHeader: getHeaderRenderers()[f.variable],
                        order: state.columns[f.variable] ? state.columns[f.variable].order : f.order,
                        width: ww[f.variable] || 100,
                        cellClassName: p => {
                            return GetBTUUID(p.row, '_source.bt_uuid') ? 'backtesting' : '';
                        },
                        ...getTableTypes(theme)[f.type]
                    }))
                    .sort((a, b) => {
                        if (state.columns[a.variable] && !state.columns[b.variable]) return -1;
                        if (state.columns[b.variable] && !state.columns[a.variable]) return 1;
                        return (a.order || 0) - (b.order || 0);
                    });
            }
            if (current.length < 1) return defaultColumns();
            return current;
        });
    }, [
        MetadataService,
        fields,
        theme,
        defaultColumns,
        shouldColSelHide,
        getHeaderRenderers,
        getCellRenderers,
        hideColumns,
        widths,
    ]);

    // returns last hour for timeline selection in BT mode
    const lastHourRangeFromJobs = useCallback((selectedBTJobs, backtestingJobs) => {
        const range = GetMomentRangeFromJobs(selectedBTJobs, backtestingJobs);
        const start = Moment(range[1]).subtract(1, 'hours');
        const lastHour = start.isBefore(range[0]) ? [start, range[1]] : range;

        return [lastHour[0].utc().toISOString(), lastHour[1].utc().toISOString()];
    }, []);

    // backward compatibility settings will be set first time only
    const applyPageState = useCallback(state => {
        if (!state) {
            return resetPage();
        }

        // need to sanitize the data coming in
        if (state.startTime) {
            state.start = state.startTime;
        }
        if (state.endTime) {
            state.end = state.endTime;
        }
        if (state.end === '' || state.end === null) delete state.end;
        if (!state.start || state.start === '' || state.start === null) {
            // defense against the dark arts of missing start dates
            state.quick = QUICK_HOUR;
        }
        if (state._quickFilter && state._quickFilter.length > 0) {
            state.quick = state._quickFilter;
        }
        if (state.keyvalFilters) {
            state.filter = state.keyvalFilters;
        }
        if (state.tags) {
            state.tagFilters = state.tags;
        }
        if (state.terms) {
            state.search = state.terms;
        }

        // only some modes are stashable
        const allowable = [ListMode, FivetupleMode, ThreetupleMode, MapMode];
        state.mode = allowable.find(a => a === state.mode) || ListMode;

        // apply quick filters
        if (state.quick) {
            state.end = undefined;
            state.start = GetISODateFromQuick(state.quick);
        }

        // map groups to group field
        state.groups = (state.groups || []).map(g => {
            if (typeof g === 'object') g = g.key || g.variable;
            return MetadataService.columnsMap[g];
        });

        // columns and sources backward compatibility
        if (state.mtdtColsSerialized) {
            const cols = JSON.parse(state.mtdtColsSerialized);
            if (cols.sortCol && cols.sortOrder) {
                state.sort = `${cols.sortCol} ${cols.sortOrder ? 'asc' : 'desc'}`;
            }
            if (cols.selDataTypes) {
                state.sources = Object.keys(cols.selDataTypes).filter(k => cols.selDataTypes[k]);
            }

            state.columns = (cols.columns || []).reduce((a, c, i) => {
                return { ...a, [c.name]: { order: i } };
            }, {});
            delete state.mtdtColsSerialized;
        }

        // columns preferences are stashed in the profile but are not part of saved filters for consistency
        const ww = { ...defaultWidths, ...widths, ...state.widths || {} };
        // update in place
        const hd = Object.assign(hideColumns, state.hideColumns, {});

        // apply column preferences, or leave alone or set to defaults if there are no columns yet
        reloadColumns(state);

        setWidths(ww);
        setHideColumns(hd);
        setMode([state.mode]);
        setPause(state.pause);
        setSort(state.sort || defaultSort);
        setGroups(state.groups || []);
        setGrouping((state.groups || []).length > 0);
        setQuick(state.quick);
        setSearch(state.search);
        setTagsOp(state.tagsOp || 'or');
        setHasAlerts(state.hasAlerts || false);
        setTagFilters(state.tagFilters);
        setFilter(state.filter);
        setRange(state.quick === 'job'
            ? lastHourRangeFromJobs(state.selectedBTJobs, backtestingJobs)
            : [state.start, state.end]);
        setSources(state.sources || ['conn']);
        // bootstrap condition - don't update if we don't have the support setting yet.
        const btui = _get(systemSettings, 'support.backtesting.ui');
        setBacktesting(btui === undefined ? state.backtesting : !btui ? BTMODE_NONE
            : state.backtesting !== undefined ? state.backtesting
                : BTMODE_LIVE);
        setSelectedBTJobs(state.selectedBTJobs || []);
        setSelectedCmcs(state.selectedCmcs);
        setCmc(getCmc(state.cmc));
    }, [
        hideColumns,
        resetPage,
        widths,
        getCmc,
        setCmc,
        reloadColumns,
        MetadataService,
        systemSettings,
        backtestingJobs,
        lastHourRangeFromJobs,
        defaultSort,
    ]);

    const getCurrentState = useCallback(state => {
        return {
            sort: currentMode === HistoryMode ? defaultSort : sort,
            search,
            start: range[0],
            end: range[1],
            quick,
            filter,
            tagFilters,
            tagsOp,
            hasAlerts,
            groups: groups ? groups.map(g => g.variable) : undefined,
            sources,
            mode: currentMode,
            backtesting,
            selectedBTJobs,
            columns: visibleColumns,
            selectedCmcs,
            cmc: (cmc || {}).uuid,
            ...state,
        };
    }, [
        visibleColumns,
        filter,
        groups,
        currentMode,
        quick,
        range,
        search,
        sort,
        sources,
        tagFilters,
        tagsOp,
        hasAlerts,
        cmc,
        selectedCmcs,
        backtesting,
        selectedBTJobs,
        defaultSort,
    ]);

    // sort if the column is visible, otherwise find the first visible sortable column
    const safeSort = useCallback((c, o, fallback) => {
        const col = MetadataService.columnsMap[c];
        if (col && !col.hide) {
            return [c, o];
        }

        if (fallback) {
            return fallback;
        }

        let i = 0;
        let next = columns[i];
        while (!next.sortable && i < columns.length) {
            i++;
            next = columns[i];
        }
        return [next.variable, o];
    }, [columns, MetadataService]);

    // sort if the column is visible
    const safeSetSort = useCallback((c, o, fallback) => {
        const ss = safeSort(c, o, fallback);
        setSort(`${ss[0]} ${ss[1]}`);
    }, [safeSort]);

    const handleOnCellClick = (params, e) => {
        if (window.getSelection().type === 'Range') {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const navigateToMtdt = useCallback(mtdt => {
        if (data.length < 1) return;
        let i = 0;
        if (!mtdt) {
            mtdt = data[0];
        } else {
            i = data.findIndex(a => _get(a, '_source.bricata.event_uuid') === _get(mtdt, '_source.bricata.event_uuid'));
        }

        const prev = i > 0 ? data[i - 1] : undefined;
        const next = i >= 0 && i < data.length ? data[i + 1] : undefined;
        setCurrentMtdt({ ...mtdt, rowIndex: i });
        setPrevMtdt(prev);
        setNextMtdt(next);
        setMode(current => {
            if (current.length > 0 && current[current.length - 1] === DetailMode) {
                // update in place to avoid re-render
                current[current.length - 1] = DetailMode;
                return current;
            }
            return [...current, DetailMode];
        });

        // add to history - don't add dup but bump to the top of the list
        const state = { ...getCurrentState(), quick: undefined, columns: undefined };
        const ogProfile = getProfile(profile);
        const history = (profile.lastViewed || []);
        i = history.findIndex(h => h.uuid === _get(mtdt, '_source.bricata.event_uuid'));
        if (i >= 0) history.splice(i, 1);
        history.unshift({ timestamp: mtdt._source.timestamp, uuid: _get(mtdt, '_source.bricata.event_uuid'), state });
        history.splice(42);

        updateProfile(profile, { ...ogProfile, lastViewed: history });
    }, [
        data,
        updateProfile,
        profile,
        getProfile,
        getCurrentState,
    ]);

    const handleViewMtdt = (rowData, prev, next) => {
        if (tagging || showTags || showIncidents || showMitre) return;
        if (_get(rowData, 'row.count', 0) > 0) navigateToGroup(rowData.row);
        else navigateToMtdt(rowData.row);
    };

    const handleModeChange = useCallback(v => {
        if (showTags || showIncidents || showMitre) return;
        MetadataService.cancelAll();

        setMode(current => {
            // go to the first mtdt in the page
            if (v === DetailMode) {
                navigateToMtdt();
                return [...current, v];
            }

            const lastMode = current.length < 1 ? ListMode : current[current.length - 1];
            if (v === FivetupleMode) {
                setGroups([MetadataService.columnsMap['bro_log.community_id']]);
                setGrouping(true);
            } else if (v === ThreetupleMode) {
                setGroups([MetadataService.columnsMap['bro_log.clisrv_id']]);
                setGrouping(true);
            } else if (v === ListMode) {
                if (lastMode === ThreetupleMode || lastMode === FivetupleMode) {
                    setGroups(gCurrent => (gCurrent && gCurrent.length > 0) ? [] : gCurrent);
                    setGrouping(false);
                }
            }
            return [...current, v];
        });
    }, [
        MetadataService,
        navigateToMtdt,
        showTags,
        showIncidents,
        showMitre,
    ]);

    const navigateToGroup = useCallback(mtdt => {
        const groupFilter = MetadataService.filterFromGroups(mtdt, groups);
        (groupFilter.nodes || []).map(f => ({ ...f, expanded: true }));
        groupFilter.expanded = true;

        // don't want to double kick the table
        setFilter(current => {
            if (current && (current.nodes || []).length > 0) {
                if (current.operator === 'And') {
                    groupFilter.nodes.push(...current.nodes);
                } else {
                    groupFilter.nodes.push(current);
                }
            }
            return groupFilter;
        });

        setGroups(current => (current && current.length > 0) ? [] : current);
        setGrouping(false);
        setMode(current => [...current, ListMode]);
        setShowFilterTimeout({ exit: 4200 });

        // reset timeout back to zero so we can use this again
        setTimeout(() => {
            setShowFilterTimeout();
        }, 4200);
    }, [MetadataService, groups]);

    const handleOnQuickFilter = useCallback(q => {
        setQuick(q.key);
        let daRange;
        if (q.key === 'job') {
            daRange = lastHourRangeFromJobs(selectedBTJobs, backtestingJobs);
        } else {
            // Keep end as undefined for live auto-refresh
            const start = GetISODateFromQuick(q.key);
            daRange = [start, undefined];
        }
        setRange(daRange);
        setRefresh();
    }, [selectedBTJobs, backtestingJobs, lastHourRangeFromJobs]);

    const handleSelectBTJobs = useCallback(jobs => {
        setRange(current => quick === QUICK_JOB
            ? lastHourRangeFromJobs(jobs, backtestingJobs) : current);
        setSelectedBTJobs(jobs);
    }, [quick, backtestingJobs, lastHourRangeFromJobs]);

    const handleSetBacktesting = useCallback((v, pq, pj, pr) => {
        setBacktesting(v);
        setQuick(v === BTMODE_BT ? QUICK_JOB : pq);
        setSelectedBTJobs(v === BTMODE_BT ? pj : []);
        setRange(current => v === BTMODE_BT
            ? lastHourRangeFromJobs(pj, backtestingJobs)
            : pq ? [GetISODateFromQuick(pq), undefined]
                : pr);

        // if in detail view, go back to list view
        setCurrentMtdt();
        setMode(current => {
            const lastMode = current.length < 1 ? ListMode : current[current.length - 1];
            return lastMode === DetailMode ? [...current, ListMode] : current;
        });
    }, [backtestingJobs, lastHourRangeFromJobs]);

    const handleBacktestingJobsUpdated = useCallback(() => {
        setRange(current => {
            if (quick === QUICK_JOB) {
                // noise reduction as much as possible
                const btJobsRange = lastHourRangeFromJobs(selectedBTJobs, backtestingJobs);
                if ((current || []).length < 2) return btJobsRange;
                current[0] = btJobsRange[0];
                current[1] = btJobsRange[1];
                return current;
            }
            return current;
        });
    }, [quick, selectedBTJobs, backtestingJobs, lastHourRangeFromJobs]);

    const onHistoryDelete = useCallback((e, mtdt) => {
        e.preventDefault();
        e.stopPropagation();
        const handler = _get(tableAPIRef, 'current.state.handlers.onHistoryDelete');
        handler && handler(mtdt);
    }, [tableAPIRef]);

    const handleTimelineMouseover = useCallback(selection => {
        setQuick();
        const start = selection.length > 0 ? Moment(selection[0]).utc().toISOString() : undefined;
        const end = selection.length > 1 ? Moment(selection[1]).utc().toISOString() : undefined;
        setRange([start, end]);
    }, []);

    const handleDonutClick = filter => {
        handleAddFilter(_get(filter, 'groups', []).map(g => (
            { variable: NoKeyword(g.key), value: g.value, }
        )));
    };

    const handleSearchFilterChange = useCallback(({ filter: f, search: s }) => {
        f !== undefined && setFilter(f);
        s !== undefined && setSearch(s);
    }, []);

    const handleSelectSavedFilter = useCallback(f => {
        setSavedFilter(f ? f.name : '');
        applyPageState(f ? f.data : f);
    }, [applyPageState]);

    const handleUpsertSavedFilter = useCallback(f => {
        setFilter(_get(f, 'data.filter'));
        setSearch(_get(f, 'data.search'));
        const state = getCurrentState({
            filter: _get(f, 'data.filter'),
            search: _get(f, 'data.search')
        });
        MetadataService.upsertSavedFilter({ ...f, data: state })
            .then(res => setSavedFilters(res)).catch(handleError);
    }, [getCurrentState, MetadataService]);

    const handleDeleteSavedFilter = useCallback(f => {
        MetadataService.deleteSavedFilter(f.uuid)
            .then(res => setSavedFilters(res)).catch(handleError);
        setSavedFilter(current => current === f.name ? '' : current);
    }, [MetadataService]);

    const handleOnFilterImport = useCallback(data => {
        // backward compatibility
        if (data.data) {
            data = { ...data.data, type: data.type };
        }
        if (data.type !== 'metadata') {
            toast.error(`Cannot import '${data.type}' file into this view`);
            console.log(data);
            return;
        }
        setSavedFilter();
        applyPageState(data);
    }, [applyPageState]);

    const handleOnFilterExport = useCallback(f => {
        if (!f) return;

        const state = {
            ...getCurrentState(),
            filter: f.filter,
            search: f.search,
            type: 'metadata',
        };
        SaveFile(
            JSON.stringify(state),
            `${savedFilter || 'metadata-filter'}.json`,
            'text/json;charset=utf-8;'
        );
    }, [getCurrentState, savedFilter]);

    const handleStartChange = useCallback(start => {
        if (!start || !start.isValid()) return;
        setQuick();
        const newRange = [start ? start.utc().toISOString() : undefined, range[1]];
        setRangeDebounce(newRange);
    }, [setRangeDebounce, range]);

    const handleEndChange = useCallback(end => {
        if (end && !end.isValid()) return;
        setQuick();
        const newRange = [range[0], end ? end.utc().toISOString() : undefined];
        setRangeDebounce(newRange);
    }, [setRangeDebounce, range]);

    const handleStartIncidents = useCallback(() => {
        tableAPIRef.current.selectRows(
            [...tableAPIRef.current.getSelectedRows().keys()],
            false
        );
        setSelection([]);
        setTagging(true);
    }, [tableAPIRef]);

    const handleIncidentSelection = useCallback((rows, incident) => {
        IncidentService.addMetadata(incident.uuid, rows.map(d => ({
            uuid: d._source.bricata.event_uuid,
            timestamp: d._source.timestamp,
            source: d._source.bricata.event_source
        })));
    }, []);

    const handleFinishIncidents = useCallback(incident => {
        setTagging(false);
        if ((selection || []).length < 1) return;

        tableAPIRef.current.selectRows(selection, false);
        incident && handleIncidentSelection(selection.map(s => tableAPIRef.current.getRow(s)), incident);
    }, [tableAPIRef, selection, handleIncidentSelection]);

    const handleStartMitre = useCallback(() => {
        tableAPIRef.current.selectRows(
            [...tableAPIRef.current.getSelectedRows().keys()],
            false
        );
        setSelection([]);
        setTagging(true);
    }, [tableAPIRef]);

    const handleMitreSelection = useCallback((rows, mitre) => {
        if ((groups || []).length > 0) {
            let f = {
                operator: 'Or',
                nodes: rows.map(a => MetadataService.filterFromGroups(a, groups)),
            };
            if (filter && (filter.nodes || []).length > 0) {
                f = { operator: 'And', nodes: [filter, f] };
            }
            setMtdtsMitre({ id: mitre }, {
                search,
                start: range[0],
                end: range[1],
                filter: f,
                tagFilters,
                tagsOp,
                sources,
                hasAlerts,
                backtesting,
                selectedBTJobs,
                jobCreated,
            }).catch(handleError);
        } else {
            rows.forEach(mtdt => {
                setMtdtMitre(_get(mtdt, '_source.bricata.event_uuid'), mitre, cmc).catch(handleError);
            });
        }
        // update visible rows immediately
        tableAPIRef.current.updateRows(rows.map(d => {
            d._source.bricata.mitre_category = mitre;
            return { _source: d._source };
        }));
    }, [
        tableAPIRef,
        groups,
        MetadataService,
        range,
        filter,
        tagFilters,
        tagsOp,
        sources,
        hasAlerts,
        backtesting,
        selectedBTJobs,
        jobCreated,
        cmc,
        search,
        setMtdtsMitre,
        setMtdtMitre,
    ]);

    const handleFinishMitre = useCallback(mitre => {
        setTagging(false);
        if ((selection || []).length < 1) return;

        tableAPIRef.current.selectRows(selection, false);
        mitre && handleMitreSelection(selection.map(s => tableAPIRef.current.getRow(s)), mitre);
    }, [tableAPIRef, selection, handleMitreSelection]);

    const handleStartTagging = () => {
        // clear currently selected rows
        tableAPIRef.current.selectRows(
            [...tableAPIRef.current.getSelectedRows().keys()],
            false
        );
        setSelection([]);
        setTagging(true);
    };

    const handleFinishTagging = tag => {
        setTagging(false);
        if ((selection || []).length < 1) return;

        tableAPIRef.current.selectRows(selection, false);
        tag && handleTagSelection(selection.map(s => tableAPIRef.current.getRow(s)), tag);
    };

    const handleTagSelection = (rows, tag) => {
        if ((groups || []).length > 0) {
            let f = {
                operator: 'Or',
                nodes: rows.map(a => MetadataService.filterFromGroups(a, groups)),
            };
            if (filter && (filter.nodes || []).length > 0) {
                f = { operator: 'And', nodes: [filter, f] };
            }
            MetadataService.tagMtdts(tag, {
                search,
                start: range[0],
                end: range[1],
                filter: f,
                tagFilters,
                tagsOp,
                sources,
                hasAlerts,
                backtesting,
                selectedBTJobs,
                jobCreated,
                cmc,
            }).catch(handleError);
        } else {
            rows.forEach(mtdt => {
                MetadataService.tagMtdt(tag, mtdt, cmc).catch(handleError);
            });
        }
        // update visible rows immediately
        tableAPIRef.current.updateRows(rows.map(d => {
            if (!_get(d, '_source.bricata.tag')) _set(d, '_source.bricata.tag', []);
            if (!d._source.bricata.tag.find(t => t === tag)) {
                d._source.bricata.tag.unshift(tag);
            }
            return { _source: d._source };
        }));
    };

    const handleTagSources = (uuids, tag) => {
        if (!tag) return;
        MetadataService.tagSources(tag, uuids, cmc).catch(handleError);
        tableAPIRef.current.updateRows(uuids.reduce((a, c) => {
            const r = tableAPIRef.current.getRow(c);
            if (!r) return a;

            if (!_get(r, '_source.bricata.tag')) _set(r, '_source.bricata.tag', []);
            if (!r._source.bricata.tag.find(t => t === tag)) {
                r._source.bricata.tag.unshift(tag);
            }
            return [...a, r];
        }, []));
    };

    const handleUntagSources = (uuids, tag) => {
        if (!tag) return;
        MetadataService.untagSources(tag, uuids, cmc).catch(handleError);
        tableAPIRef.current.updateRows(uuids.reduce((a, c) => {
            const r = tableAPIRef.current.getRow(c);
            if (!r) return a;

            // remove immediately from selected row
            const i = _get(r, '_source.bricata.tag', []).findIndex(t => t === tag);
            if (i >= 0) {
                r._source.bricata.tag.splice(i, 1);
            }
            return [...a, r];
        }, []));
    };

    const handleTagFilter = async tag => {
        await MetadataService.tagMtdts(tag, {
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            sources,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }).catch(handleError);
        // kick the table
        setFilter({ ...filter });
    };

    const handleUntagFilter = async tag => {
        await MetadataService.untagMtdts(tag, {
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            sources,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }).catch(handleError);
        setFilter({ ...filter });
    };

    const handleMitreFilter = async mitre => {
        await setMtdtsMitre(mitre, {
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            sources,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }).catch(handleError);
        setFilter({ ...filter });
    };

    const handleUnmitreFilter = async () => {
        await clearMtdtsMitre({
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            sources,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }).catch(handleError);
        setFilter({ ...filter });
    };

    const handleTagSelect = tag => {
        const i = (tagFilters || []).findIndex(t => t === tag);
        if (i >= 0) return;
        setTagFilters([...(tagFilters || []), tag]);
    };

    // outgoing to grid
    const getSortModel = () => {
        const ss = sort.split(' ');
        return [{ field: MetadataService.columnsMap[ss[0]].field, sort: ss[1] }];
    };

    // incoming from grid
    const handleOnSort = p => {
        if (!p.length) return;
        const f = MetadataService.fieldsMap[p[0].field];
        if (!f) return;
        setSort(`${f.variable} ${p[0].sort}`);
    };

    const handleSelectionChange = (newSelection) => {
        if (tagging) setSelection(newSelection);
    };

    const handleFilterDest = value => {
        const nodes = value.map(v => ({ variable: 'bro_log.id.resp_h', value: v, type: 'ip' }));
        handleAddFilter([{ operator: 'Or', nodes, expanded: true }]);
    };

    const handleFilterSrc = value => {
        const nodes = value.map(v => ({ variable: 'bro_log.id.orig_h', value: v, type: 'ip' }));
        handleAddFilter([{ operator: 'Or', nodes, expanded: true }]);
    };

    const handleAddWantSource = () => {
        setSources(current => ([...current, ...wantSource]));
        setWantSource();
    };

    const getSorted = useCallback(() => {
        const d = groups.length > 0 ? 'count desc' : defaultSort;
        let ss = (sort || d).split(' ');
        // defense against the dark arts of sorting on count when we're not grouping
        if (groups.length < 1 && ss[0] === 'count') ss = d.split(' ');
        // defense against the dark arts of sorting on something else when we're grouping
        if (groups.length > 0) ss[0] = 'count';
        return ss;
    }, [sort, defaultSort, groups.length]);

    const handleAddFilter = useCallback(f => {
        if (!f || f.length < 1) return;

        // incoming job filters will just get added to the filtered backtesting jobs list
        const jobs = f.filter(ff => ff.type === 'bt_job_uuid' || ff.variable === 'bt_uuid')
            .map(ff => ff.value);
        f = f.reduce((a, c) => c.type === 'bt_job_uuid' || c.variable === 'bt_uuid'
            ? a : a.concat([c]), []);

        if (jobs.length > 0) {
            setSelectedBTJobs(current => {
                if (!current) return jobs;

                jobs.forEach(j => {
                    if (current.findIndex(c => c === j) < 0) current.push(j);
                });

                return jobs;
            });
        }

        if (!f || f.length < 1) return;

        const variables = f.map(ff => ff.variable);
        variables.forEach(v => {
            const field = MetadataService.fieldsMap[v] || MetadataService.fieldsMap[`_source.${v}`];
            const fieldSources = MetadataService.fieldSources[v];
            if (field && field.source && !field.sticky && Object.keys(fieldSources).length > 0) {
                if (!(sources || []).some(s => fieldSources[s])) {
                    // suggest the first source that has the field
                    const want = Object.keys(fieldSources)[0];
                    setWantSource(current => [...(current || []), want]);
                }
            }
        });

        setFilter(current => {
            if (current && (current.nodes || []).length > 0) {
                if (current.operator === 'And') {
                    const same = (c, n) =>
                        c.variable && n.variable === c.variable && n.value === c.value;
                    f = f.filter(n => current.nodes.findIndex(c => same(c, n)) < 0);
                    current.nodes.push(...f);
                    f = current.nodes;
                } else {
                    f.push(current);
                }
            }
            return { operator: 'And', root: true, nodes: f, expanded: true }; // uf;
        });
        setShowFilterTimeout({ exit: 4200 });
        // reset timeout back to zero so we can use this again
        setTimeout(() => {
            setShowFilterTimeout();
        }, 4200);
    }, [sources, MetadataService.fieldsMap, MetadataService.fieldSources]);

    // recursive paging backwards until done
    const getGeoData = useCallback(({ refresh, interval, page, next }) => {
        setLoading(refresh || true);
        setDone(true);
        MetadataService.getGeoHistory({
            sources,
            search,
            start: refresh || range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            interval,
            page,
            startover: !next,
            cmc,
        }).then(res => {
            console.debug('got geodata', 'refresh:', refresh, 'page:', page, 'end:', res.end, 'next_page:', res.next_page);
            setRefresh(current => page ? current : (res.end || current));

            let ndata = (res || {});
            setGeodata(cdata => {
                const clear = Object.keys((cdata || {}).locations || {}).length === 0;
                const end = page ? cdata.end : res.end;
                ndata = MergeNodes(cdata || {}, ndata);
                ndata.end = end;
                ndata.start = refresh || range[0];
                ndata.next_page = res.next_page;
                ndata.clear = clear;
                ndata.live = !!refresh;
                return ndata;
            });

            // remove me
            res.controller && res.controller.signal.addEventListener('abort', () => {
                console.debug('aborted', res.controller.signal);
            });
            return res || {};
        }, err => console.error('geo history error', err))
            .then(res => {
                if (res === undefined || res === null || !res.next_page || res.next_page === page) {
                    console.debug('all done with geo history');
                    setDone(true);
                    return;
                }
                // abort
                if (res.controller.signal.aborted) {
                    console.debug('geohistory backfill aborted');
                    setDone(true);
                    return;
                }
                console.debug('one more page - interval:', res.interval, 'next_page:', res.next_page);
                // start paging backwards to retrieve the rest of the history
                return getGeoData({
                    interval: res.interval,
                    page: res.next_page,
                    refresh,
                    next: true,
                });
            })
            .catch(err => console.error('geo history next page error', err))
            .finally(() => setLoading(false));
    }, [
        filter,
        range,
        search,
        tagFilters,
        tagsOp,
        hasAlerts,
        sources,
        cmc,
        MetadataService,
        backtesting,
        selectedBTJobs,
        jobCreated,
    ]);

    const getActivityData = useCallback(({ page, refresh }) => {
        setDone(false);
        setTotal();
        setLoading(refresh || true);
        const ss = getSorted();
        // this is here to force a dependency
        //console.debug('mappings', mappings);
        MetadataService.getData({
            sort: ss[0],
            order: ss[1],
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            groups,
            sources,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }, page !== undefined).then(res => {
            setRefreshRate(REFRESH_RATE);
            res = res || {};
            // track latest query timestamp for next fetch call
            if (!page) {
                setRefresh(current => res.since || current);
                setAsof(res.asof);
            }
            let ndata = (res.data || []);
            if (ndata.length < 1 && !refresh) {
                setDone(true);
                return;
            }
            setData(cdata => {
                if (refresh) {
                    // add to the top of the list
                    ndata = [...ndata, ...cdata];

                    // get rid of dups in the merged list
                    ndata = _uniqBy(ndata, d => _get(d, '_source.bricata.event_uuid'));
                } else if (page) {
                    // add to the bottom of the list
                    ndata = [...cdata, ...ndata];

                    // get rid of dups in the merged list
                    ndata = _uniqBy(ndata, d => _get(d, '_source.bricata.event_uuid'));
                }
                // no mas, and prune excess (good for refresh loop)
                setDone(ndata.length > MTDT_LIMIT);

                return ndata;
            });
            setMsg(res.timedOut ? 'Query timed out' : `Search completed in ${res.took} milliseconds`);
            // remove me
            res.controller && res.controller.signal.addEventListener('abort', () => {
                console.debug('aborted', res.controller.signal);
            });
        }, handleError).finally(() => setLoading(false));
    }, [
        search,
        range,
        filter,
        tagFilters,
        tagsOp,
        hasAlerts,
        groups,
        sources,
        getSorted,
        cmc,
        MetadataService,
        mappings,
        backtesting,
        selectedBTJobs,
        jobCreated,
    ]);

    const getHistoryData = useCallback(() => {
        const profile = getProfile(props.profile);
        const history = (profile.lastViewed || []);

        // first time with the new GUI, fetching history will be slow
        // after that we'll start using timestamp to limit the query
        let updated = false;
        let start = history.reduce((a, c) => {
            updated = updated || !c.timestamp;
            const ct = c.timestamp ? Moment(c.timestamp) : Moment().subtract(1, 'years');
            return !a || ct.isBefore(a) ? ct : a;
        }, false);
        start = (start || Moment().subtract(1, 'years')).utc().toISOString();
        updated && updateProfile(props.profile, { ...profile, lastViewed: history });
        return MetadataService.getSelectMtdts({
            start,
            uuids: history.map(h => h.uuid),
            cmc,
        }).then(res => {
            const data = (res.data || []);
            setData(data);
            // backward compatibility one time only
            // add timestamp history to speed things up for the next times
            if (history.length > 0 && !history[0].timestamp) {
                history.forEach(h => {
                    const a = data.find(a => _get(a, '_source.bricata.event_uuid') === h.uuid);
                    if (a) h.timestamp = a.timestamp;
                });
                updateProfile(props.profile, { ...profile, lastViewed: history });
            }
            setTotal(data.length);
        }, handleError).finally(() => setLoading(false));
    }, [updateProfile, getProfile, MetadataService, cmc, props.profile]);

    // recursive, will break on signal or when it runs out of data
    const fetchSparkline = useCallback(async (data, controller, timelines) => {
        if (data.length < 1) return Promise.resolve(timelines);
        const d = data.slice(0);
        let f = MetadataService.filterFromGroups(d[0], groups);
        if (f && (f.nodes || []).length > 0) {
            f = { operator: 'And', nodes: [filter, f] };
        }

        // remove me
        controller && controller.signal.addEventListener('abort', () => {
            console.debug('aborted', controller.signal);
        });

        // skip empty timelines
        if (d[0].count <= 1) {
            // next!
            if (d.length > 0) {
                return fetchSparkline(d.slice(1), controller, timelines);
            }
            return Promise.resolve(timelines);
        }

        return MetadataService.getTimeline({
            search,
            start: range[0],
            end: range[1],
            filter: f,
            tagFilters,
            tagsOp,
            groups,
            controller,
            sources,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        })
            .then(res => {
                const uuid = _get(d[0], '_source.bricata.event_uuid');
                timelines = {
                    ...timelines,
                    [uuid]: res,
                };
                const row = tableAPIRef.current.getRow(uuid);
                tableAPIRef.current.updateRows([{ ...row, timeline: res }]);
                // next! (if the original query hasn't been canceled)
                if (!controller.signal.aborted && d.length > 0) {
                    return fetchSparkline(d.slice(1), controller, timelines);
                }
                return timelines;
            }, handleError);
    }, [
        tableAPIRef,
        search,
        range[0],
        range[1],
        tagFilters,
        tagsOp,
        hasAlerts,
        groups,
        sources,
        cmc,
        filter,
        MetadataService,
        backtesting,
        selectedBTJobs,
        jobCreated,
    ]);

    const fetchGroupData = useCallback(() => {
        const isGrouping = groups.length > 0;
        const ss = getSorted();
        MetadataService.getGroups({
            order: ss[1],
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            sources,
            groups,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }).then(res => {
            setRefresh(range[1] || new Date().toISOString());
            // dedupe (grouping by tags can cause duplicate groups)
            const seen = {};
            const data = (res.data || []).filter(a => {
                const uuid = _get(a, '_source.bricata.event_uuid');
                if (seen[uuid]) {
                    return false;
                }
                seen[uuid] = true;
                return true;
            });

            // set group data from data, we don't need keyword index for filtering from the donut
            setGroupedData(data.map(g => ({ ...g, key: NoKeyword(g.key) })));

            // remove me
            res.controller && res.controller.signal.addEventListener('abort', () => {
                console.debug('aborted', res.controller.signal);
            });

            // if we have groups we can use this call for both the donut and the table
            if (isGrouping) {
                setData(data); // .map(r => ({ ...r, id: _get(r, '_source.bricata.event_uuid') })));
                setTotal(data.length);

                if (!hideColumns.activity) {
                    fetchSparkline(data, res.controller, {});
                }
            } else {
                setTotal(data.reduce((a, c) => a + c.count, 0));
            }
        }, handleError).finally(() => setLoading(false));
    }, [
        search,
        range,
        filter,
        tagFilters,
        tagsOp,
        hasAlerts,
        sources,
        groups,
        fetchSparkline,
        cmc,
        MetadataService,
        hideColumns.activity,
        backtesting,
        selectedBTJobs,
        jobCreated,
        getSorted,
    ]);

    const fetchHistoryData = useCallback(() => {
        setDone(false);
        setLoading(false);
        setCurrentMtdt();
        setNextMtdt();
        setPrevMtdt();
        setGeodata({});
        setPage(1);
        if (currentMode === HistoryMode) {
            setData([]);
            getHistoryData();
        }
    }, [currentMode, getHistoryData]);

    const handleError = error => {
        // this is a happy path - we're canceling outdated requests all the time
        if (error.code === DOMException.ABORT_ERR) return;

        setLoading(false);
        // stop surfacing up errors
        // toast.error(`There was an error processing your request, ${error}`);
        console.error(`There was an error processing your request, ${error}`);
    };

    // recursive, keep paging backwards until done
    const doFetchLzyTimeline = useCallback(({ start, end, cancel, took, interval, absEnd }) => {
        // metadata timeline start and end don't change - just the timeline selection slider
        const t = new Date();
        MetadataService.GetLazyTimeline({
            search,
            filter,
            tagFilters,
            tagsOp,
            sources,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            end,
            start,
            cmc,
            cancel,
            took,
            interval,
        }).then(res => {
            if (!res) {
                console.log('no timeline data');
                return;
            };
            // we got canceled
            if (_get(res, 'controller.signal.aborted')) {
                console.log('lazy timeline is canceled');
                return;
            }

            setTimeline(cdata => {
                let ndata = res.objects || [];
                if (end) {
                    ndata = ndata.filter(d => Moment(d.key_as_string).isBefore(Moment(end)));
                }
                ndata = ndata.concat(end ? cdata.data : []);
                return {
                    data: ndata,
                    start: res.start,
                    end: absEnd,
                    progress: res.next ? CalculateProgress(res.start, absEnd, res.next) : 100,
                };
            });

            // next!
            if (res.next) {
                doFetchLzyTimeline({
                    end: res.next,
                    start,
                    took: Math.abs(new Date() - t),
                    interval: res.interval,
                    absEnd,
                });
            }
        }).catch(err => {
            // if we're canceled ignore error
            console.log('error caught', err);
            if (err.code === DOMException.ABORT_ERR) return;
            handleError(err);
        });
    }, [
        search,
        filter,
        tagFilters,
        tagsOp,
        hasAlerts,
        sources,
        cmc,
        MetadataService,
        backtesting,
        selectedBTJobs,
        jobCreated,
    ]);

    const fetchLazyTimeline = useCallback(async () => {
        setTimeline({ data: [], progress: 100 });
        doFetchLzyTimeline({ cancel: true });
    }, [
        doFetchLzyTimeline,
    ]);

    const fetchLazyBTTimeline = useCallback(async () => {
        if (backtesting !== BTMODE_BT) return;
        const fullrange = GetRangeFromJobs(selectedBTJobs, backtestingJobs);
        setTimeline({ data: [], progress: 100 });
        doFetchLzyTimeline({
            cancel: true,
            start: fullrange[0],
            end: fullrange[1],
            absEnd: fullrange[1],
        });
    }, [
        doFetchLzyTimeline,
        selectedBTJobs,
        backtestingJobs,
        backtesting,
        // range,
    ]);

    const handleColumnsUpdated = useCallback(({ columns, sources, sort, mappings }) => {
        setMappings(mappings);

        // if we're in history view the 'remove column' should be visible
        const r = columns.findIndex(c => c.variable === 'remove');
        if (r >= 0) columns[r].hide = currentMode !== HistoryMode;

        setColumns(columns);
        setSources(sources);

        // might be a new column - trust the process
        if (sort && MetadataService.fieldsMap[sort.field]) {
            setSort(`${MetadataService.fieldsMap[sort.field].variable} ${sort.sort}`);
        }

        if (groups.length > 0) {
            setHideColumns(current => {
                // update in place for noise reduction
                columns
                    .filter(c => c.grouping)
                    .forEach(c => {
                        if (c.hide) current[c.variable] = true;
                        else delete current[c.variable];
                    });
                return current;
            });
        }
    }, [setMappings, currentMode, groups, MetadataService]);

    const handleRemoveTagFilter = useCallback(tag => {
        setTagFilters(current => {
            const i = (current || []).findIndex(t => t === tag);
            if (i < 0) return current;
            current.splice(i, 1);
            return [...current];
        });
    }, []);

    const handleOnWantPolicySelect = policy => {
        setWantPolicy(current => ({ ...current, policy }));
    };

    const handleOnWantPolicy = () => {
        handleTuneAlert(wantPolicy.filter, wantPolicy.policy);
        setWantPolicy();
    };

    const handleTuneAlert = useCallback(async (filter, policy) => {
        const gator = await PolicyService.filterToGator(filter || EMPTY_FILTER);
        const seedGator = {
            gator,
            action: 'forget',
            scope: 'in',
            target: 'metadata',
        };
        navigate(`/policies/sensor/data_tuning/${policy}?${ParamsSerializer(seedGator)}`);
    }, [navigate]);

    // use page filter
    const handleTune = useCallback(() => {
        if (!gatorPerms.read) return;

        PolicyService.getPolicies('data_tags').then(res => {
            const policies = (res || []).filter(p => {
                return MC2CHECK || p.source === 'cmc';
            });
            if (policies.length < 1) return;
            setWantPolicy({
                filter,
                policies: policies.map(p => p.name),
                policy: policies[0].name,
            });
        });
    }, [filter]);

    const fetchFields = useCallback(async () => {
        Object.entries(cmcs || { undefined: '' })
            .map(([uuid, name]) => ({ uuid, name }))
            .forEach(cmc => {
                MetadataService.cacheFields(true, cmc).then(res => {
                    if (res && res !== 'no cmc') {
                        // fields are merged with existing in MetadataService
                        setFields(current => res || current);
                    }
                }).catch(handleError);
            });
    }, [MetadataService, cmcs]);

    const handleCmcsSelect = useCallback(c => {
        setCmc(current => {
            // don't kick data if we don't need to
            if (current && current.uuid === c) return current;
            return getCmc(c);
        });
    }, [setCmc, getCmc]);

    // remove receives just uuid
    const handleCmcsRemove = useCallback(c => {
        if (!c) return;
        setSelectedCmcs(current => {
            const i = (current || []).findIndex(cc => cc === c);
            if (i < 0) return current;
            current.splice(i, 1);
            return [...current];
        });
    }, []);

    // to confuse the enemy, add receives an object
    const handleCmcsAdd = useCallback(c => {
        if (!c) return;
        setSelectedCmcs(current => {
            const i = (current || []).findIndex(cc => cc === c.uuid);
            if (i >= 0) return current;
            return [...(current || []), c.uuid];
        });
    }, []);

    // update sort when grouping is reset
    useEffect(() => {
        setSort(current => {
            let nSort = current;
            if (groups && groups.length > 0) {
                nSort = 'count desc';
            }
            if (!groups || groups.length < 1) {
                nSort = 'timestamp desc';
            }
            return nSort;
        });
    }, [groups]);

    // try to keep current sort when mode changes
    useEffect(() => {
        // no need to touch sort when switching to detail mode
        if (currentMode === DetailMode) return;
        setSort(current => {
            const cs = current.split(' ');
            let ss;

            // defense against the dark arts of count sorting when we're not grouping
            if (currentMode === HistoryMode) {
                ss = safeSort(cs[0], cs[1], ['remove', 'desc']);
            } else if (groups && groups.length > 0) {
                ss = safeSort(cs[0], cs[1], ['count', 'desc']);
            } else {
                ss = safeSort(cs[0], cs[1], defaultSort.split(' '));
            }

            return `${ss[0]} ${ss[1]}`;
        });
    }, [groups, safeSort, currentMode, defaultSort]);

    // cmc selections (open tabs) hook when cmc list changes
    useEffect(() => {
        if ((cmcs || []).length === 0) {
            setSelectedCmcs([]);
            return;
        }
        setSelectedCmcs(current => {
            if ((current || []).length === 0) return current;
            return current.filter(sc => (cmcs || {})[sc]);
        });
    }, [cmcs]);

    // cmc hook when cmc selection list changes
    useEffect(() => {
        if ((selectedCmcs || []).length === 0) {
            setCmc();
            return;
        }
        setCmc(current => {
            if (!current) return getCmc(selectedCmcs[0]);
            if (!selectedCmcs.find(c => c === current.uuid)) return getCmc(selectedCmcs[0]);
            return current;
        });
    }, [selectedCmcs, setCmc, getCmc]);

    // selected cmc hook when cmc list changes
    useEffect(() => {
        if (cmc) {
            if (!(cmcs || {})[cmc.uuid]) return;
            setSelectedCmcs(current => {
                if (!(current || []).find(c => c === cmc.uuid)) {
                    // add cmc to the tab list
                    return [...(current || []), cmc.uuid];
                }
                return current;
            });
        }
    }, [cmc, fetchFields, cmcs, MetadataService]);

    const handleShare = useCallback((p) => {
        const f = p && p.uuid ? {
            operator: 'And',
            nodes: [{ variable: 'bricata.event_uuid', value: p.uuid }]
        } : filter;

        const params = p && p.uuid
            ? {
                filter: JSON.stringify(f),
                sources,
                quick,
                start: range[0],
                end: range[1],
                cmc: (cmc || {}).uuid,
            }
            : {
                filter: f ? JSON.stringify(f) : undefined,
                groups: groups.length > 0 ? (groups || []).map(g => g.variable) : undefined,
                sources,
                search,
                sort,
                tagFilters,
                tagsOp,
                hasAlerts,
                quick,
                start: range[0],
                end: range[1],
                cmc: (cmc || {}).uuid,
            };
        CopyToClipboard(`${window.location.protocol}//${window.location.host}/views/metadata?${ParamsSerializer(params)}`);
        toast.info('Link was copied to clipboard', { progress: false });
    }, [filter, groups, sources, search, sort, tagFilters, tagsOp, hasAlerts, quick, range, cmc]);

    const handleSaveReport = useCallback(async format => {
        const settings = await ReportsService.getReportMaxRows().catch(handleError);
        toastId.current = toast(<ReportsToast
            settings={settings}
            format={format}
            total={total}/>, { autoClose: false });
        const ss = sort.split(' ');
        MetadataService.generateReport({
            sort: ss[0],
            order: ss[1],
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            groups,
            sources,
            hasAlerts,
            backtesting,
            selectedBTJobs,
            jobCreated,
            columns: columns.filter(c => !c.hide).map(c => c.variable),
            format,
            total,
            cmc,
        }).then(res => {
            toast.dismiss(toastId.current);
        }, err => {
            toast.dismiss(toastId.current);
            if (err.status === 502) {
                toast.info("We'll continue working on your report. When it's done it will be available from the Reports page in the System menu.");
            } else {
                console.log(err);
                toast.error("We're unable to process your report.");
            }
        }).catch(handleError).finally(() => toast.dismiss(toastId.current));
    }, [
        total,
        columns,
        sort,
        search,
        range,
        filter,
        tagFilters,
        tagsOp,
        hasAlerts,
        groups,
        sources,
        cmc,
        MetadataService,
        backtesting,
        selectedBTJobs,
        jobCreated,
    ]);

    const getTableTypes = theme => ({
        sev: { typs: 'number' },
        int: { type: 'number' },
        timestamp: {
            type: 'dateTime',
            valueFormatter: ({ value }) => {
                const d = Moment(value);
                return d.isValid() ? Moment(d).format(theme.date.long) : value;
            }
        },
        date: {
            type: 'dateTime',
            valueFormatter: ({ value }) => {
                const d = Moment(value);
                return d.isValid() ? Moment(d).format(theme.date.long) : value;
            }
        },
    });

    const handleShowColumnSelector = useCallback(() => {
        fetchFields();
        setShowColumnsSel(true);
    }, [fetchFields]);

    const isLive = useCallback(() => {
        if (!init || range[1]) return false;

        if (currentMode === ListMode) return sort === 'timestamp desc' && groups.length < 1;
        return false;
    }, [init, groups.length, sort, currentMode, range]);

    useInterval(() => {
        getActivityData({ refresh })
        if (!MC2CHECK) {
            //Refresh Timeline
            doFetchLzyTimeline({ cancel: true });
            //Refresh Donut
            fetchGroupData();
        }
    }, (!pause && !loading && props.live !== false && isLive()) ? refreshRate : null);

    const readRouteQuery = useCallback(() => {
        const routeQuery = new URLSearchParams(location.search);
        if (routeQuery.toString().length < 1) return {};
        const state = {};

        // making route params backward compatible so existing links still work
        if (routeQuery.has('keyval')) state.filter = routeQuery.get('keyval');
        else state.filter = routeQuery.get('filter');
        if (routeQuery.has('group')) state.groups = routeQuery.getAll('group');
        else state.groups = routeQuery.getAll('groups');
        if (routeQuery.has('source')) state.sources = routeQuery.getAll('source');
        else state.sources = routeQuery.getAll('sources');
        if (routeQuery.has('terms')) state.search = routeQuery.get('terms');
        else state.search = routeQuery.get('search');
        if (routeQuery.has('sort_col') && routeQuery.has('sort_order')) {
            state.sort = `${routeQuery.get('sort_col')} ${routeQuery.get('sort_order')}`;
        } else state.sort = routeQuery.get('sort');
        if (routeQuery.has('tags')) state.tagFilters = routeQuery.getAll('tags');
        else state.tagFilters = routeQuery.getAll('tagFilters');
        if (routeQuery.has('tags_op')) state.tagsOp = routeQuery.get('tags_op');
        else state.tagsOp = routeQuery.get('tagsOp');
        if (routeQuery.has('start_time')) state.start = routeQuery.get('start_time');
        else state.start = routeQuery.get('start');
        if (routeQuery.has('end_time')) state.end = routeQuery.get('end_time');
        else state.end = routeQuery.get('end');
        state.quick = routeQuery.get('quick');
        state.hasAlerts = routeQuery.get('hasAlerts') === true || routeQuery.get('hasAlerts') === 'true';
        state.cmc = routeQuery.get('cmc');
        if (state.cmc) state.selectedCmcs = [state.cmc];
        if (routeQuery.has('backtesting')) state.backtesting = parseInt(routeQuery.get('backtesting'), 10);
        if (routeQuery.has('selectedBTJobs')) state.selectedBTJobs = routeQuery.getAll('selectedBTJobs');

        // we don't want to leave the page hanging
        if (state.sources.length < 1) delete state.sources;
        // don't keep nulls
        if (!state.search) state.search = '';
        if (!state.filter) delete state.filter;
        if (!state.sort) state.sort = state.groups.length ? 'count desc' : defaultSort;
        if (!state.start) delete state.start;
        if (!state.end) delete state.end;
        if (!state.cmc) delete state.cmc;

        if (state.filter) {
            try {
                state.filter = JSON.parse(state.filter);
            } catch (err) {
                console.log('ignoring filter param', routeQuery.get('filter'));
                state.filter = EMPTY_FILTER;
            }
        }

        if (_get(state.filter, 'nodes.length', 0) > 0) {
            // expand all nodes
            const expand = f => {
                (f.nodes || []).forEach(expand);
                if (f.operator) f.expanded = true;
            };
            expand(state.filter);

            setShowFilterTimeout({ exit: 4200 });
            // reset timeout back to zero so we can use this again
            setTimeout(() => {
                setShowFilterTimeout();
            }, 4200);
        }
        return state;
    }, [location.search, defaultSort]);

    const toggleFilterbar = useCallback(() => {
        setHideFilterbar(current => !current);
    }, []);

    const toggleTimelinebar = useCallback(() => {
        setHideTimelinebar(current => !current);
    }, []);

    const setPageState = useCallback(() => {
        const profile = getProfile(props.profile);
        setHideFilterbar(_get(profile, 'hideFilterbar', false));
        setHideTimelinebar(_get(profile, 'hideTimelinebar', false));
        const state = _get(profile, 'state', { quick: QUICK_HOUR });
        const params = readRouteQuery();
        // backward compatibility
        if (profile.mtdtColsSerialized) state.mtdtColsSerialized = profile.mtdtColsSerialized;

        // query parameters take priority, but state has column preferences
        applyPageState({ ...state, ...params });
    }, [getProfile, applyPageState, readRouteQuery, props.profile]);

    // select cmc hook
    useEffect(() => {
        if (MC2CHECK) {
            (async () => {
                const cmcs = await CMCsDataService.getNames();
                setCmcs(cmcs);
            })();
        }

        if (gatorPerms.read) {
            (async () => {
                PolicyService.getPolicies('data_tags').then(res => {
                    const policies = (res || []).filter(p => {
                        return MC2CHECK || p.source === 'cmc';
                    });
                    setCanTune(policies.length > 0);
                });
            })();
        } else {
            setCanTune(false);
        }
    }, []);

    // initial hook
    useEffect(() => {
        if (MC2CHECK && !cmcs) return;

        if (!props.hideFilterbar) {
            (async () => {
                const d = await MetadataService.cacheSavedFilters().catch(handleError);
                setSavedFilters(d);
            })();
        }

        // check for incidents
        if (!MC2CHECK) {
            (async () => {
                !props.miniToolbar && incidentPerms.write && IncidentService.getAll()
                    .then(res => setHaveIncidents((res || []).length > 0));
            })();
        }
        fetchFields();

        return () => {
            setInit(false);
            MetadataService.cancelAll();
        };
    }, [cmcs,
        fetchFields,
        MetadataService,
        props.hideFilterbar,
        props.miniToolbar,
    ]);

    // size hook
    useEffect(() => {
        // I <3 pixle pushing
        const fb = hideFilterbar ? 15 : 80;
        const tb = hideTimelinebar ? 15 : TIMELINE_HEIGHT;
        const both = MC2CHECK && hideFilterbar ? -10
            : hideFilterbar && hideTimelinebar ? -15 : 0;
        const footer = 23;
        setTableHeight(Math.max(
            height - (props.headerSize || (PAGE_HEADER + fb + tb + both)) - footer,
            200
        ));
    }, [height, hideTimelinebar, hideFilterbar, props.headerSize]);

    const maybeReturnToSelection = useCallback(() => {
        if (mode.length < 3) return false;

        const currentMode = mode[mode.length - 1];
        if (currentMode === ListMode || currentMode === HistoryMode) {
            const prevMode = mode[mode.length - 2];
            if (prevMode === DetailMode) {
                // are we returning from the same place we came from?
                if (mode[mode.length - 3] === currentMode && currentMtdt) {
                    tableAPIRef.current.scrollToIndexes({ rowIndex: currentMtdt.rowIndex });
                    return true;
                }
            }
        }
        return false;
    }, [mode, currentMtdt, tableAPIRef]);

    // english hook
    useEffect(() => {
        const bt = GetBacktestingEnglish(selectedBTJobs, backtestingJobsLite);

        const sf = savedFilter ? `Saved Filter: ${savedFilter}` : '';

        const f = MetadataService.toEnglish(filter, true, sensorNames, assets, backtestingJobsLite);

        const combined = [sf, f, bt]
            .filter(f => f.length && f !== '<span></span>')
            .join(' <b>AND</b> ');
        setEnglish(combined);
    }, [filter, savedFilter, sensorNames, assets, MetadataService, backtestingJobsLite, selectedBTJobs]);

    useEffect(() => {
        // update backtesting bit when data becomes available
        const btui = _get(systemSettings, 'support.backtesting.ui', false);
        setBacktesting(current => {
            return current === BTMODE_NONE
                ? btui ? BTMODE_LIVE : BTMODE_NONE
                : current;
        });
    }, [(systemSettings)]);

    // once metadata fields are cached we can set the rest of the page
    useEffect(() => {
        // bootstrap
        if (MC2CHECK && !cmcs) return;
        else if (fields.length < 1) return;

        // do this only once
        if (init) return;
        setPageState();
        setInit(true);
    }, [init, fields, cmcs, setPageState]);

    // visibleColumns only change when absolutely necessary.
    // reduce side effects and spamming profile settings
    useEffect(() => {
        const nvm = columns
            .filter(c => !c.hide)
            .reduce((a, c, i) => ({ ...a, [c.variable]: { order: i } }), {});

        setVisibleColumns(current => {
            if (Object.keys(nvm).length !== Object.keys(current || {}).length) return nvm;

            const same = Object.entries(current || {}).reduce((a, [k, v], i) => {
                return a && !!(nvm[k]) && nvm[k].order === v.order;
            }, true);

            return same ? current : nvm;
        });
    }, [columns]);

    // query changed hook for timeline,
    // two hooks so we don't needlessly update the timeline on date changes
    useEffect(() => {
        if (!init) return;
        if (MC2CHECK) return;
        if (isBacktesting) return;
        fetchLazyTimeline();
    }, [init, fetchLazyTimeline, isBacktesting, range]);

    useEffect(() => {
        if (!init) return;
        if (MC2CHECK) return;
        if (backtesting !== BTMODE_BT) return;
        fetchLazyBTTimeline();
    }, [init, fetchLazyBTTimeline, backtesting]);

    // mode changed hook for data (donut and table)
    useEffect(() => {
        if (!init || !_get(tableAPIRef, 'current') || currentMode === DetailMode || currentMode === HistoryMode) return;
        if (maybeReturnToSelection()) return;

        // kick the data query to trigger the hook below
        setFilter(current => ({ ...(current || {}) }));
    }, [init, tableAPIRef, currentMode, maybeReturnToSelection]);

    // query changed hook for donut and table
    useEffect(() => {
        if (!init || currentMode === HistoryMode || !range[0]) return;

        // don't add currentMode to dependencies, we don't want to trigger this when mode changes
        // the hook above will take care of that if needed
        setDone(false);
        setLoading(false);
        setNextMtdt();
        setPrevMtdt();
        setGeodata({});
        setPage(1);
        if (currentMode === MapMode) {
            getGeoData({});
        } else {
            fetchGroupData();
            setData([]);
            if (!groups || groups.length < 1) {
                // if we don't have groups, we need to make an additional call to fetch activity
                getActivityData({});
            }
        }
    }, [init, getActivityData, fetchGroupData, groups, getGeoData, range, tableAPIRef]);

    // query changed hook for history data (less deps)
    useEffect(() => {
        if (!init || currentMode !== HistoryMode) return;
        if (maybeReturnToSelection()) return;

        setSort('remove desc');

        fetchHistoryData();
    }, [init, currentMode, maybeReturnToSelection, fetchHistoryData]);

    // next page hook
    const prevPage = usePrevious(page);
    useEffect(() => {
        if (!init || !page || page < 2 || page === prevPage) return;
        getActivityData({ page });
    }, [init, page, getActivityData, prevPage]);

    const handleColumnReorder = params => {
        setColumns(tableAPIRef.current.getAllColumns());
    };

    const handleColumnResize = params => {
        setWidths(current => {
            return {
                ...current,
                [params.colDef.variable]: params.width,
            };
        });
    };

    const handleScrollEnd = useCallback(params => {
        if (init && !loading && !done && (currentMode === ListMode || currentMode === ThreetupleMode || currentMode === FivetupleMode) && (groups || []).length < 1) {
            setPage(p => p + 1);
        }
    }, [init, loading, done, currentMode, groups]);

    const handleCloseBackdrop = (e, reason) => {
        console.log('reason', reason);
    };

    // hook for updating table components, e.g., toolbar, footer
    useEffect(() => {
        if (!init) return;
        // update table ref for use by grouping toolbar
        tableAPIRef.current.state.params = {
            groups,
            mode: currentMode,
            tagFilters,
            tagsOp,
            hasAlerts,
            grouping,
            asof,
            msg,
            total, //  || (done ? (data || []).length : undefined),
            fields: MetadataService.fieldsMap,
            cmcs,
            selectedCmcs,
            cmc,
            profile: 'metadata',
        };
        if (tableAPIRef.current) {
            // add handlers for grouping toolbar
            tableAPIRef.current.state.handlers = {
                onTagAction: () => { setShowTags(true); setShowMitre(false) },
                onRemoveTag: currentMode === HistoryMode ? undefined : handleRemoveTagFilter,
                onTagsOpSelect: currentMode === HistoryMode ? undefined : setTagsOp,
                onColumnsSel: handleShowColumnSelector,
                onMitreAction: perms.write ? () => {
                    setShowMitre(true);
                    setShowTags(false);
                } : undefined,
            };
            if (!props.miniToolbar) {
                tableAPIRef.current.state.handlers = {
                    ...tableAPIRef.current.state.handlers,
                    onAddGroup: currentMode === HistoryMode ? undefined : g => {
                        // reset sorting when grouping changes
                        if (groups.length < 1) safeSetSort('count', 'desc');
                        // defense against the dark arts
                        if (groups.find(gg => gg.variable === g.variable)) return;
                        setGroups([...groups, g]);
                        // get out of tuple view
                        setMode(current => {
                            const pMode = current.length > 0 ? current[current.length - 1] : ListMode;
                            return pMode === ThreetupleMode || pMode === FivetupleMode ? [...current, ListMode] : current;
                        });
                    },
                    onClearGroups: currentMode === HistoryMode ? undefined : () => {
                        const ds = defaultSort.split(' ');
                        safeSetSort(ds[0], ds[1]);
                        setGroups(current => (current && current.length > 0) ? [] : current);
                        // get out of tuple view
                        setMode(current => {
                            const pMode = current.length > 0 ? current[current.length - 1] : ListMode;
                            return pMode === ThreetupleMode || pMode === FivetupleMode ? [...current, ListMode] : current;
                        });
                    },
                    onRemoveGroup: currentMode === HistoryMode ? undefined : g => {
                        setGroups(current => {
                            const i = (current || []).findIndex(c => g.variable === c.variable);
                            if (i < 0) return current;
                            current.splice(i, 1);
                            return [...current];
                        });

                        // get out of tuple view
                        setMode(current => {
                            const pMode = current.length > 0 ? current[current.length - 1] : ListMode;
                            return pMode === ThreetupleMode || pMode === FivetupleMode ? [...current, ListMode] : current;
                        });
                    },
                    onIncidentsAction: haveIncidents && (groups || []).length < 1 && currentMode === ListMode
                        ? () => setShowIncidents(true) : undefined,
                    onGrouping: currentMode === HistoryMode ? undefined : setGrouping,
                    onHistoryDelete: row => {
                        const profile = getProfile(props.profile);
                        const history = (profile.lastViewed || []);
                        const i = history.findIndex(h => h.uuid === _get(row, '_source.bricata.event_uuid'));
                        if (i >= 0) history.splice(i, 1);
                        updateProfile(props.profile, { ...profile, lastViewed: history });
                        // remove from list immediately
                        setData(current => {
                            const i = current.findIndex(d => _get(d, '_source.bricata.event_uuid') === _get(row, '_source.bricata.event_uuid'));
                            const ret = [...current];
                            if (i >= 0) ret.splice(i, 1);
                            return ret;
                        });
                    },
                    onReport: (currentMode === DetailMode || currentMode === HistoryMode) ? undefined : handleSaveReport,
                    onTune: (currentMode === DetailMode || currentMode === HistoryMode || !gatorPerms.write || !canTune)
                        ? undefined : handleTune,
                    onShare: (currentMode === DetailMode || currentMode === HistoryMode) ? undefined : handleShare,
                    onPause: isLive() ? () => setPause(current => !current) : undefined,
                    onFetch: (currentMode === DetailMode || currentMode === HistoryMode || currentMode === MapMode || (!isBacktesting && range[1])) ? undefined
                        : sort !== defaultSort || groups.length > 0
                            ? () => getActivityData({})
                            : () => getActivityData({ refresh }),
                    onViewAlerts: currentMode === ListMode && groups.length < 1 &&
                        (sort === defaultSort || sort === 'timestamp asc')
                        ? () => setHasAlerts(current => !current) : undefined,
                    onCmcSelect: MC2CHECK ? handleCmcsSelect : undefined,
                    onCmcRemove: MC2CHECK ? handleCmcsRemove : undefined,
                    onCmcsAdd: MC2CHECK ? handleCmcsAdd : undefined,
                };
            }
        }
    }, [
        init,
        defaultSort,
        tableAPIRef,
        getProfile,
        updateProfile,
        currentMode,
        groups,
        tags,
        tagsOp,
        hasAlerts,
        tagFilters,
        grouping,
        data,
        asof,
        msg,
        total,
        safeSetSort,
        sort,
        onHistoryDelete,
        handleRemoveTagFilter,
        getActivityData,
        range,
        refresh,
        handleTune,
        handleShare,
        handleSaveReport,
        done.fieldsMap,
        handleShowColumnSelector,
        haveIncidents,
        handleCmcsSelect,
        handleCmcsRemove,
        handleCmcsAdd,
        props.miniToolbar,
        MetadataService,
        done,
        cmc,
        cmcs,
        selectedCmcs,
        canTune,
        props.profile,
        isBacktesting,
    ]);

    // hook for updating group colors and count bar
    useEffect(() => {
        setMaxGroup(groupedData.reduce((a, c) => a > c.count ? a : c.count, 0));
        setGroupKeys(Array.from(new Set([...Severities, ...groupedData.map(g => g.key)])));
    }, [groupedData]);

    // hook for responding to group changes (show/hide columns and sort)
    useEffect(() => {
        if (!tableAPIRef.current) return;
        const headerRenderers = getHeaderRenderers();
        const cellRenderers = getCellRenderers();

        // show/hide grouping columns
        setColumns(current => {
            // should return default here?
            if (current.length < 1) return current;

            // don't force a re-render unless something changes
            let changed = false;

            let cols = [...current];

            // update objects in place to reduce noise,
            // and while we're looping find a couple of indexes
            cols.forEach((c, i) => {
                const f = MetadataService.fieldsMap[c.field] || {};
                cols[i].sortable = (groups || []).length > 0
                    ? (f.variable === 'count') : (f.sortable || f.indexed);

                if (c.variable === 'db_timestamp') {
                    const chide = backtesting !== BTMODE_BT;
                    if (cols[i].columnSelectorHidden !== chide) {
                        cols[i].columnSelectorHidden = chide;
                        changed = true;
                    }
                    const hide = backtesting !== BTMODE_BT || hideColumns.db_timestamp;
                    if (cols[i].hide !== hide) {
                        cols[i].hide = hide;
                        changed = true;
                    }
                }
            });

            // show/hide backtesting column
            const b = cols.findIndex(c => c.variable === 'bt_uuid');
            if (b >= 0) {
                const hide = backtesting !== BTMODE_BT;
                if (cols[b].hide !== hide) {
                    cols[b].hide = backtesting !== BTMODE_BT;
                    cols = arrayMove(cols, b, 0);
                    changed = true;
                }
            }

            const a = cols.findIndex(c => c.variable === 'activity');
            if (a >= 0) {
                let move = false;
                const hide = groups.length < 1 || currentMode === HistoryMode || !!(hideColumns.activity);
                if (cols[a].hide !== hide) {
                    cols[a].hide = hide;
                    move = true;
                }
                const chide = groups.length < 1;
                if (cols[a].columnSelectorHidden !== chide) {
                    cols[a].columnSelectorHidden = chide;
                    move = true;
                }
                if (move) {
                    cols = arrayMove(cols, a, 0);
                    changed = true;
                }
            }

            const c = cols.findIndex(c => c.variable === 'count');
            if (c >= 0) {
                let move = false;
                const hide = groups.length < 1 || currentMode === HistoryMode || hideColumns.count;
                if (cols[c].hide !== hide) {
                    cols[c].hide = hide;
                    move = true;
                }
                const chide = groups.length < 1;
                if (cols[c].columnSelectorHidden !== chide) {
                    cols[c].columnSelectorHidden = groups.length < 1;
                    move = true;
                }
                if (move) {
                    cols = arrayMove(cols, c, 0);
                    changed = true;
                }
            }

            const r = cols.findIndex(c => c.variable === 'remove');
            if (r >= 0) {
                cols[r].renderCell = p =>
                    <IconButton size="small"
                        color="secondary"
                        onClick={e => onHistoryDelete(e, p.row)}
                        aria-label="delete from history">
                        <DeleteIcon fontSize="inherit"/>
                    </IconButton>;
                const hide = currentMode !== HistoryMode;
                if (cols[r].hide !== hide) {
                    cols[r].hide = hide;
                    changed = true;
                    cols = arrayMove(cols, r, cols.length);
                }
            }

            if (!changed) return current;

            // update renderers
            Object.keys(mappings).forEach(m => {
                const c = cols.findIndex(c => c.variable === m);
                if (c >= 0) {
                    cols[c].renderHeader = headerRenderers[m];
                    cols[c].renderCell = cellRenderers[m];
                }
            });

            return cols;
        });
        // disabled 'view alert' bubble when grouping
        const detail = modes.findIndex(m => m.key === DetailMode);
        modes[detail].disabled = groups && groups.length > 0;
    }, [
        MetadataService.fieldsMap,
        tableAPIRef,
        groups,
        currentMode,
        hideColumns.count,
        hideColumns.activity,
        hideColumns.db_timestamp,
        onHistoryDelete,
        mappings,
        getHeaderRenderers,
        getCellRenderers,
        backtesting,
    ]);

    useEffect(() => {
        if (!tableAPIRef || !tableAPIRef.current || !tableAPIRef.current.updateColumns) {
            return;
        }
        tableAPIRef.current.updateColumns(columns);
    }, [tableAPIRef, columns]);

    // profile update hook
    useEffect(() => {
        if (!init) return;
        // ignore first time profile is loaded
        if (!updatedRef.current) {
            updatedRef.current = true;
            return;
        }
        const profile = getProfile(props.profile);
        const state = getCurrentState();
        const updated = {
            ...profile,
            hideFilterbar,
            hideTimelinebar,
            state: {
                widths,
                hideColumns,
                ...state,
            }
        };

        // remove obsolete fields from profile
        const {
            data,
            filter: f,
            mode: m,
            tagsOp: to,
            sortCol,
            sortOrder,
            startTime,
            endTime,
            mtdtColsSerialized,
            _quickFilter,
            _mode,
            _groups,
            showEnglish,
            ...rest
        } = updated;
        updateProfile(props.profile, rest);
    }, [
        init,
        sort,
        hideColumns,
        getProfile,
        updateProfile,
        widths,
        getCurrentState,
        hideFilterbar,
        hideTimelinebar,
        props.profile,
    ]);

    return (
        <React.Fragment>
            <GroupsContext.Provider value={{
                maxGroup,
                groupKeys
            }}>
                {<main className={clsx(classes.content)}>
                    <Box style={{ position: 'absolute', right: 10, top: -35 }}>
                        {hideFilterbar && !props.hideFilterbar &&
                            <Button
                                color="primary"
                                onClick={toggleFilterbar}
                                aria-label="show filter panel"
                                startIcon={<ExpandMoreIcon />}>
                                Filters
                            </Button>}
                        {hideTimelinebar &&
                         !props.hideTimelinebar &&
                         !MC2CHECK &&
                            <Button
                                color="primary"
                                onClick={toggleTimelinebar}
                                aria-label="show timeline"
                                startIcon={<ExpandMoreIcon />}>
                                Timeline
                            </Button>}
                    </Box>
                    {/* FilterSearchCombo, backtesting, time selection panel */}
                    <FilterBar
                        gHideFilterbar={props.hideFilterbar}
                        { ...{
                            hideFilterbar,
                            hideTimelinebar,
                            handleSearchFilterChange,
                            showFilterTimeout,
                            filter,
                            search,
                            savedFilters,
                            setShowFilter,
                            showFilter,
                            fields,
                            perms,
                            handleSelectSavedFilter,
                            handleUpsertSavedFilter,
                            handleDeleteSavedFilter,
                            handleOnFilterExport,
                            handleOnFilterImport,
                            backtesting,
                            handleSetBacktesting,
                            handleBacktestingJobsUpdated,
                            selectedBTJobs,
                            handleSelectBTJobs,
                            english,
                            toggleFilterbar,
                            handleOnQuickFilter,
                            handleStartChange,
                            handleEndChange,
                            quick,
                            start: (range || []).length > 0 ? range[0] : undefined,
                            end: (range || []).length > 1 ? range[1] : undefined,
                            label: 'Metadata',
                        } } />

                    {!MC2CHECK && <Collapse
                        in={!hideTimelinebar && !props.hideTimelinebar}
                        style={{
                            marginLeft: -10,
                            display: props.hideTimelinebar ? 'hide' : '',
                        }}>
                        <Grid
                            container
                            spacing={1}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            className="nowraping"
                            wrap="nowrap">
                            <Grid item>
                                <Grid container direction="row" justifyContent="flex-start"
                                    alignItems="center" wrap="nowrap">
                                    <TimelineChartWithSlider
                                        dataservice={MetadataService}
                                        data={timeline.data}
                                        start={timeline.start}
                                        end={timeline.end}
                                        height={TIMELINE_HEIGHT}
                                        selection={range}
                                        onSelection={handleTimelineMouseover}
                                        isBT={isBacktesting}
                                        progress={timeline.progress}/>
                                    <DonutChart
                                        data={groupedData}
                                        size={TIMELINE_HEIGHT - 10}
                                        onclick={handleDonutClick}
                                        isBT={isBacktesting}/>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <IconButton
                                    title="Hide Timeline"
                                    onClick={toggleTimelinebar}
                                    aria-label="Hide Timeline"
                                    size="large">
                                    <ExpandLessIcon fontSize="inherit"/>
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Collapse>}
                    {showColumnsSel && <ColumnSelector
                        show={showColumnsSel}
                        columns={columns}
                        sources={sources}
                        mappings={mappings}
                        fields={fields}
                        sortCol={sort.split(' ').length && sort.split(' ')[0]}
                        sortOrder={sort.split(' ').length && sort.split(' ')[1]}
                        anchorEl={toolbarRef.current}
                        onClickAway={() => setShowColumnsSel(false)}
                        onOK={handleColumnsUpdated}/>}
                    <TagList
                        show={showTags}
                        cmc={cmc}
                        placement='bottom-end'
                        onClickAway={() => setShowTags(false)}
                        anchorEl={toolbarRef.current}
                        onTagSelect={currentMode !== HistoryMode ? handleTagSelect : undefined}
                        onStartTagging={currentMode !== MapMode && perms.write
                            ? handleStartTagging : undefined}
                        onFinishTagging={currentMode !== MapMode && perms.write
                            ? handleFinishTagging : undefined}
                        onUntagFilter={currentMode !== MapMode && currentMode !== HistoryMode && perms.write && !props.miniToolbar
                            ? handleUntagFilter : undefined}
                        onTagFilter={currentMode !== MapMode && currentMode !== HistoryMode && perms.write && !props.miniToolbar
                            ? handleTagFilter : undefined}/>
                    <MitreList
                        show={showMitre}
                        placement='bottom-end'
                        onClickAway={() => setShowMitre(false)}
                        anchorEl={toolbarRef.current}
                        onStartMitre={currentMode !== MapMode && perms.write
                            ? handleStartMitre : undefined}
                        onFinishMitre={currentMode !== MapMode && perms.write
                            ? handleFinishMitre : undefined}
                        onUnmitreFilter={currentMode !== MapMode && currentMode !== HistoryMode && perms.write && !props.miniToolbar
                            ? handleUnmitreFilter : undefined}
                        onMitreFilter={currentMode !== MapMode && currentMode !== HistoryMode && perms.write && !props.miniToolbar
                            ? handleMitreFilter : undefined}/>
                    <IncidentResponseList
                        show={showIncidents}
                        placement='bottom-end'
                        onClickAway={() => setShowIncidents(false)}
                        anchorEl={toolbarRef.current}
                        onSelect={currentMode !== HistoryMode ? handleTagSelect : undefined}
                        onStartSelection={currentMode !== MapMode ? handleStartIncidents : undefined}
                        onFinishSelection={currentMode !== MapMode ? handleFinishIncidents : undefined}/>
                </main>}
                <div ref={toolbarRef}/>
                <BubbleTabs
                    tabs={props.modes || modes}
                    selected={currentMode}
                    onClick={handleModeChange}
                    static={true}>
                    {currentMode === DetailMode && <div>
                        <MetadataDetail
                            onPrev={prevMtdt ? () => navigateToMtdt(prevMtdt) : undefined}
                            onNext={nextMtdt ? () => navigateToMtdt(nextMtdt) : undefined}
                            onFilter={props.hideFilterbar ? undefined : handleAddFilter}
                            mtdt={currentMtdt}
                            onTag={perms.write ? handleTagSources : undefined}
                            onUntag={perms.write ? handleUntagSources : undefined}
                            onTune={gatorPerms.write ? handleTuneAlert : undefined}
                            onShare={handleShare}
                            params={{
                                start: range[0],
                                end: range[1],
                                filter,
                                sources,
                                search,
                                tagFilters,
                                tagsOp,
                                cmc,
                            }}
                            header={props.headerSize || (height - tableHeight)}
                            cmc={cmc}
                            isBT={GetBTUUID(currentMtdt, '_source.bt_uuid') !== undefined}/>
                    </div>}
                    {currentMode === MapMode && <div className={classes.mapview}>
                        <GeomapToolbar
                            tagFilters={tagFilters}
                            tagsOp={tagsOp}
                            onTagAction={() => setShowTags(true)}
                            onRemoveTag={handleRemoveTagFilter}
                            onTagsOpSelect={setTagsOp}
                            onFetch={range[1] ? undefined : () => getGeoData({ refresh })}
                            cmc={cmc}
                            cmcs={cmcs}
                            onSetCmc={setCmc}/>
                        <GeoMap data={geodata}
                            onFilterSrc={handleFilterSrc}
                            onFilterDest={handleFilterDest}
                            srcStr="Orig IP"
                            destStr="Resp IP"
                            size={{ width: width - 100, height: tableHeight - 10 }}
                            isBT={isBacktesting}/>
                    </div>}
                    <Grid style={{
                        margin: 8,
                        display: currentMode === MapMode || currentMode === DetailMode
                            ? 'none' : ''
                    }}
                    container
                    direction="row"
                    justifyContent="center" alignItems="center">
                        <Grid item xs={12} className={classes.table}
                            style={{ height: tableHeight }}>
                            {init && <DataGridPro
                                components={{
                                    Header: TableToolbar,
                                    LoadingOverlay,
                                    NoRowsOverlay: MC2CHECK && !cmc
                                        ? NoCMCLoadingOverlay : NoRowsOverlay,
                                    Footer: TableFooter,
                                }}
                                apiRef={tableAPIRef}
                                loading={loading === true}
                                columns={columns}
                                rows={data}
                                columnBuffer={5}
                                rowBuffer={100}
                                rowHeight={38}
                                checkboxSelection={tagging}
                                sortingMode={props.sortingMode || 'server'}
                                sortingOrder={['desc', 'asc']}
                                onSortModelChange={handleOnSort}
                                sortModel={getSortModel()}
                                onError={e => console.error(e)}
                                disableMultipleColumnsSorting={true}
                                onSelectionModelChange={handleSelectionChange}
                                onCellClick={handleOnCellClick}
                                onRowClick={handleViewMtdt}
                                total={total}
                                disableColumnSelector={true}
                                disableColumnMenu={true}
                                disableVirtualization={false}
                                onColumnOrderChange={handleColumnReorder}
                                onColumnResize={handleColumnResize}
                                onRowsScrollEnd={props.sortingMode === 'client' ? undefined : handleScrollEnd}
                                getRowId={r => _get(r, '_source.bricata.event_uuid')}
                            />}
                            {!init &&
                                <div className={classes.loading}>
                                    <CircularProgress color="secondary" />
                                    <Typography> Fetching Metadata Fields </Typography>
                                </div>
                            }
                        </Grid>
                    </Grid>
                </BubbleTabs>
                {/* pick a data source dialog */}
                {wantSource &&
                    <Dialog
                        onClose={handleCloseBackdrop}
                        disableEscapeKeyDown
                        maxWidth="xs"
                        aria-labelledby="add sources"
                        open={(wantSource || []).length > 0}>
                        <DialogTitle> Add Data Source </DialogTitle>
                        <DialogContent dividers>
                            The filter requires {wantSource.length < 2 ? 'an ' : ''}additional data source{wantSource.length > 1 ? 's' : ''}
                            <br/>
                            {wantSource.map(s => (
                                <Chip
                                    key={s}
                                    clickable={false}
                                    size="small"
                                    color='primary'
                                    label={s}
                                    className={classes.wantSource}/>
                            ))}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                autoFocus
                                onClick={() => setWantSource()}
                                color="primary"
                                aria-label="cancel">
                                {"Don't Add"}
                            </Button>
                            <Button
                                onClick={() => handleAddWantSource()}
                                color="primary"
                                aria-label="add">
                                Add Source{wantSource.length > 1 ? 's' : ''}
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                {/* pick a policy dialog */}
                {wantPolicy &&
                    <Dialog
                        onClose={handleCloseBackdrop}
                        disableEscapeKeyDown
                        maxWidth="xs"
                        aria-labelledby="select policy"
                        open={(wantPolicy.policies || []).length > 0}>
                        <DialogTitle> Select Data Tuning Policy </DialogTitle>
                        <DialogContent dividers>
                            Please select a Data Tuning policy <b/>
                            for the new tuning rule
                            <br/>
                            <PopupMenu
                                tooltip="select policy"
                                items={wantPolicy.policies}
                                selection={wantPolicy.policy}
                                onSelect={handleOnWantPolicySelect}
                                title={wantPolicy.policy}
                                placement={'bottom-start'}/>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                autoFocus
                                onClick={() => setWantPolicy()}
                                color="primary"
                                aria-label="cancel">
                                Cancel
                            </Button>
                            <Button
                                enabled={wantPolicy.policy}
                                onClick={() => handleOnWantPolicy()}
                                color="primary"
                                aria-label="add">
                                Add Rule
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                <Backdrop
                    className={classes.backdrop}
                    open={tagging}>
                </Backdrop>
            </GroupsContext.Provider>
        </React.Fragment>
    );
};

Metadata.propTypes = {
    perm: PropTypes.func,
    hideTimelinebar: PropTypes.bool,
    hideFilterbar: PropTypes.bool,
    modes: PropTypes.array,
    sources: PropTypes.array,
    service: PropTypes.object,
    headerSize: PropTypes.number,
    miniToolbar: PropTypes.bool,
    live: PropTypes.bool,
    sortingMode: PropTypes.string,
    profile: PropTypes.string,
};

export default Metadata;
